/**
 * Closed date intervals, and what "intersects" means for them.
 *
 * ## Interval convention (single source of truth for the Sales_Cycle rules)
 *
 * **Every interval in this module is closed on both ends: `[start, end]`.** Both
 * the start day and the end day belong to the interval.
 *
 * This is not a free choice. `design.md` defines the Initiative's stored span as
 *
 * ```sql
 * date_span daterange generated always as (daterange(start_date, end_date, '[]')) stored
 * ```
 *
 * and the calendar query filters with `date_span && daterange(:jan1, :dec31, '[]')`.
 * The `'[]'` bound flag is PostgreSQL's inclusive-inclusive form, so the database
 * already answers "do these two ranges intersect" as an inclusive test. Anything
 * else here would make the derived Sales_Cycle set disagree with the impact list
 * (R6.7) and the calendar payload, which are computed by those SQL operators.
 *
 * Consequences of the closed convention, spelled out because they are the cases
 * that usually drift:
 *
 * - **Intersection is `a.start <= b.end && a.end >= b.start`.** Both comparisons
 *   are non-strict. This is exactly R7.1's wording: a cycle qualifies when its
 *   start is "earlier than or equal to" the Initiative end date and its end is
 *   "later than or equal to" the Initiative start date.
 * - **Touching at a single day counts as intersecting.** An Initiative ending on
 *   2025-03-31 and a cycle starting on 2025-03-31 share that one day, so the
 *   cycle is derived. Under a half-open `[start, end)` convention it would not
 *   be, which is precisely the drift this comment exists to prevent.
 * - **A single-day interval (`start === end`) is non-empty** and intersects
 *   itself. An Initiative may be one day long: R8.3 blocks only an end date
 *   *earlier* than the start date, so equality is legal input here.
 * - **There is no empty interval in this model.** A closed interval always
 *   contains at least its start day, so a "zero-length" interval cannot be
 *   represented. `end < start` is not an empty interval, it is malformed input;
 *   see {@link isWellFormedDateInterval}. Sales_Cycle definitions are further
 *   constrained by the database (`check (end_date > start_date)`, R6.3), so a
 *   cycle is always at least two days long.
 *
 * Tasks 6.3 (overlap and gap detection) and any SQL touching `daterange` must
 * agree with the three bullets above.
 *
 * The *convention* is deliberately local to `src/salescycle/`, and only the
 * convention: the closed-interval reading is what the Sales_Cycle rules and the
 * `daterange(..., '[]')` columns share, and a second copy of it elsewhere would
 * become a second, subtly different one. The date representation underneath it is
 * not local - `PlainDate`, its comparisons and the day successor gap detection
 * needs all come from `src/date/`, so no part of this area implements calendar
 * arithmetic of its own.
 */
import { comparePlainDates, isOnOrAfter, isOnOrBefore, plainDateYear, } from '../date/index.js';
/** Builds a closed interval from its two inclusive bounds. */
export function dateInterval(start, end) {
    return { start, end };
}
/**
 * Whether the interval is well formed, that is `start <= end`.
 *
 * `start === end` is well formed: it is the one-day interval. `end < start` is
 * malformed and is rejected upstream - by the hard validation rule of R8.3 for
 * Initiative dates and by the `sales_cycle_range` CHECK constraint for cycle
 * definitions - rather than being reinterpreted as an empty interval here.
 */
export function isWellFormedDateInterval(interval) {
    return isOnOrBefore(interval.start, interval.end);
}
/**
 * Whether two closed intervals share at least one day.
 *
 * The inclusive-inclusive test, equivalent to PostgreSQL
 * `daterange(a.start, a.end, '[]') && daterange(b.start, b.end, '[]')` for
 * well-formed inputs.
 *
 * Not defensive about well-formedness on purpose. Callers hold that guarantee
 * (hard validation and the database CHECK constraint), and the predicate is a
 * plain conjunction of two comparisons so that "the derived set is exactly the
 * intersection predicate" (Property 24) is checkable by reading it. Passing a
 * malformed interval yields a meaningless answer, not an exception.
 */
export function dateIntervalsIntersect(a, b) {
    return isOnOrBefore(a.start, b.end) && isOnOrAfter(a.end, b.start);
}
/** Whether the closed interval contains the given day. */
export function dateIntervalContains(interval, day) {
    return isOnOrBefore(interval.start, day) && isOnOrAfter(interval.end, day);
}
/**
 * The calendar years the closed interval touches, ascending and contiguous.
 *
 * Inclusive at both ends, so an interval from 2025-12-31 to 2026-01-01 touches
 * `[2025, 2026]` and a single-day interval touches exactly one year. Returns an
 * empty array for a malformed interval, the only place in this module where
 * malformed input is given a defined answer, because "which years does this
 * touch" has no sensible partial answer.
 */
export function dateIntervalYears(interval) {
    if (!isWellFormedDateInterval(interval)) {
        return [];
    }
    const firstYear = plainDateYear(interval.start);
    const lastYear = plainDateYear(interval.end);
    const years = [];
    for (let year = firstYear; year <= lastYear; year += 1) {
        years.push(year);
    }
    return years;
}
/**
 * Orders intervals by start day, then by end day. Used for presentation order,
 * never as part of a membership decision.
 */
export function compareDateIntervals(a, b) {
    const byStart = comparePlainDates(a.start, b.start);
    return byStart !== 0 ? byStart : comparePlainDates(a.end, b.end);
}
//# sourceMappingURL=date-interval.js.map